import { Controller } from "@hotwired/stimulus";

// Calendar filter dropdowns (Staff + Status). Recomputes the hidden sets from
// the checkbox DOM on every change and dispatches a `schedule:filters-changed`
// document event the operations-schedule host applies before setData().
//
// The Staff dropdown is meaningless in Day view (each staff is a column), so
// it is hidden reactively on the host's `schedule:view-changed` event.
export default class extends Controller {
  static targets = [
    "staffFilter", "staffTrigger", "staffPanel", "staffChevron", "staffSummary", "staff",
    "statusFilter", "statusTrigger", "statusPanel", "statusChevron", "statusSummary", "status",
  ];

  // Mirrors FiltersComponent::DEFAULT_STATUSES.
  DEFAULT_STATUSES = ["confirmed", "pending"];

  connect() {
    this._onOutside = (e) => { if (!this.element.contains(e.target)) this._closeAll(); };
    this._onKeydown = (e) => { if (e.key === "Escape") this._closeAll(); };
    this._onViewChanged = (e) => this._applyView(e.detail?.view);
    document.addEventListener("click", this._onOutside);
    document.addEventListener("keydown", this._onKeydown);
    document.addEventListener("schedule:view-changed", this._onViewChanged);
    this._updateSummaries();
  }

  disconnect() {
    document.removeEventListener("click", this._onOutside);
    document.removeEventListener("keydown", this._onKeydown);
    document.removeEventListener("schedule:view-changed", this._onViewChanged);
  }

  // ---- panel open/close ----

  toggleStaffPanel() { this._togglePanel("staff"); }
  toggleStatusPanel() { this._togglePanel("status"); }

  _togglePanel(group) {
    const panel = this[`${group}PanelTarget`];
    const willOpen = panel.classList.contains("hidden");
    this._closeAll();
    if (willOpen) {
      panel.classList.remove("hidden");
      this[`${group}ChevronTarget`].classList.add("rotate-180");
      this[`${group}TriggerTarget`].setAttribute("aria-expanded", "true");
    }
  }

  _closeAll() {
    ["staff", "status"].forEach((group) => {
      this[`${group}PanelTarget`].classList.add("hidden");
      this[`${group}ChevronTarget`].classList.remove("rotate-180");
      this[`${group}TriggerTarget`].setAttribute("aria-expanded", "false");
    });
  }

  // ---- selection ----

  changed() {
    this._updateSummaries();
    this._dispatch();
  }

  selectAllStaff() {
    this.staffTargets.forEach((cb) => { cb.checked = true; });
    this.changed();
  }

  clearStaff() {
    this.staffTargets.forEach((cb) => { cb.checked = false; });
    this.changed();
  }

  resetStatus() {
    this.statusTargets.forEach((cb) => {
      cb.checked = this.DEFAULT_STATUSES.includes(cb.dataset.status);
    });
    this.changed();
  }

  // ---- view gating ----

  _applyView(view) {
    if (!this.hasStaffFilterTarget) return;
    if (view === "day") {
      this.staffFilterTarget.classList.add("hidden");
      this.staffPanelTarget.classList.add("hidden");
      this.staffChevronTarget.classList.remove("rotate-180");
    } else {
      this.staffFilterTarget.classList.remove("hidden");
    }
  }

  // ---- helpers ----

  _dispatch() {
    const hiddenStaffIds = this.staffTargets
      .filter((cb) => !cb.checked)
      .map((cb) => String(cb.dataset.staffId));
    const hiddenStatuses = this.statusTargets
      .filter((cb) => !cb.checked)
      .map((cb) => cb.dataset.status);

    document.dispatchEvent(
      new CustomEvent("schedule:filters-changed", {
        detail: { hiddenStaffIds, hiddenStatuses },
      })
    );
  }

  _updateSummaries() {
    this._summarize(this.staffTargets, this.staffSummaryTarget);
    this._summarize(this.statusTargets, this.statusSummaryTarget);
  }

  _summarize(checkboxes, label) {
    const total = checkboxes.length;
    const checked = checkboxes.filter((cb) => cb.checked).length;
    label.textContent = checked === total ? "All" : `${checked} of ${total}`;
  }
}
